/**
 * Enhanced Pricing Block for Personalised Review Page
 * Adds cart-level discount integration and pricing transparency
 */

(function() {
  'use strict';

  // Debug flag
  const DEBUG = false;

  // Module-scope guard to prevent duplicate bindings
  let __prEnhancerBound = false;

  // Feature gate check
  const enhanceWrapper = document.querySelector('[data-enhance-pricing="true"]');
  if (!enhanceWrapper) {
    return; // Feature not enabled
  }

  // Required DOM elements
  const enhancedBlock = document.querySelector('[data-enhance-pricing-target]');
  const beforePriceEl = document.querySelector('#bundle-before-discount-price');
  const afterPriceEl = document.querySelector('#bundle-total-price');
  
  if (!enhancedBlock || !beforePriceEl || !afterPriceEl) {
    if (DEBUG) console.warn('PersonalisedReviewPricingEnhancer: Required DOM elements not found');
    return;
  }
  
  const mode = window.personalised?.utils?.getMode?.() || document.querySelector('#personalised-review-wrapper')?.getAttribute('data-mode');
  if (mode === 'edit' && window.location.pathname !== '/pages/manage-pack') {
    try { enhancedBlock.remove(); } catch (_) {}
    return;
  }

  // Selectors for enhanced block elements
  const selectors = {
    beforePrice: '.js-review-before-price',
    afterPrice: '.js-review-after-price',
    badge: '.compare-at-badge',
    compareAtContainer: '.compare-at-price',
    discountsList: '[data-pr-discounts]'
  };

  /**
   * Format price using review page formatter (match personalised-bundle-utils.js)
   * @param {number} cents - Price in cents
   * @param {boolean} includeCurrency - Whether to include $ prefix
   * @returns {string} Formatted price string
   */
  function formatPrice(cents, includeCurrency = true) {
    const amount = cents > 0 ? (cents / 100).toLocaleString(undefined, { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    }) : '0.00';
    
    return includeCurrency ? `$${amount}` : amount;
  }

  /**
   * Parse price text back to cents for calculations
   * @param {string} priceText - Formatted price text (e.g., "$12.34")
   * @returns {number} Price in cents
   */
  function parsePriceToCents(priceText) {
    if (!priceText) return 0;
    // Strip everything except digits, dots, and commas, then normalize
    const cleaned = priceText.replace(/[^\d.,-]/g, '').replace(',', '.');
    const val = parseFloat(cleaned);
    return Number.isFinite(val) ? Math.round(val * 100) : 0;
  }

  /**
   * Clear DOM container safely
   * @param {Element} container - Container to clear
   */
  function clearContainer(container) {
    if (container) {
      container.innerHTML = '';
    }
  }

  /**
   * Helper functions for multiple block updates
   */
  function forEachBlock(cb) {
    document.querySelectorAll('[data-enhance-pricing-target]').forEach(cb);
  }

  function writeToAllEnhancedBlocks(selector, text) {
    forEachBlock(block => {
      const el = block.querySelector(selector);
      if (el) el.textContent = text;
    });
  }

  function unhideAll(selector) {
    forEachBlock(block => {
      const el = block.matches(selector) ? block : block.querySelector(selector);
      if (el) el.removeAttribute('hidden');
    });
  }

  function hideAll(selector) {
    forEachBlock(block => {
      const el = block.matches(selector) ? block : block.querySelector(selector);
      if (el) el.setAttribute('hidden', '');
    });
  }

  function toggleCompareAt(show) {
    forEachBlock(block => {
      const cmp = block.querySelector('.compare-at-price');
      if (!cmp) return;
      cmp.style.display = show ? 'inline-flex' : 'none';
    });
  }

  function updateBadge(beforeCents, afterCents) {
    const percent = beforeCents > 0 ? Math.round((beforeCents - afterCents) / beforeCents * 100) : 0;
    forEachBlock(block => {
      const badge = block.querySelector('.compare-at-badge');
      if (!badge) return;
      if (percent > 0) {
        badge.textContent = `${percent}% off`;
        badge.style.display = '';
      } else {
        badge.textContent = '';
        badge.style.display = 'none';
      }
    });
  }

  function clearBadge() {
    forEachBlock(block => {
      const badge = block.querySelector('.compare-at-badge');
      if (badge) {
        badge.textContent = '';
        badge.style.display = 'none';
      }
    });
  }

  /**
   * Check if after price is populated in blocks
   * @returns {boolean} True if at least one block has after price
   */
  function hasAfterInBlocks() {
    let ok = false;
    document.querySelectorAll('[data-enhance-pricing-target]').forEach(block => {
      const el = block.querySelector('.js-review-after-price');
      if (el && el.textContent && el.textContent.trim().length) ok = true;
    });
    return ok;
  }

  /**
   * Debounce utility for limiting frequent calls
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} Debounced function
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Module-level order discount rate storage
  let orderLevelRate = 0;

  /**
   * Fetch cart data and process discounts
   */
  function fetchAndProcessCart() {
    fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(response => {
        if (!response.ok) throw new Error('Cart fetch failed');
        return response.json();
      })
      .then(cartData => {
        processCartDiscounts(cartData);
      })
      .catch(error => {
        if (DEBUG) console.error('PersonalisedReviewPricingEnhancer: Cart fetch error:', error);
        // Silent fallback - keep cart-derived UI hidden
      });
  }

  /**
   * Core populate logic - reads DOM and writes to all blocks
   */
  function populateFromDom() {
    try {
      // Read current prices from existing DOM
      const beforeText = beforePriceEl?.textContent?.trim?.() || '';
      const afterText = afterPriceEl?.textContent?.trim?.() || '';
      
      // Debug logging
      if (DEBUG) console.debug('[Enhancer] blocks:', document.querySelectorAll('[data-enhance-pricing-target]').length);
      if (DEBUG) console.debug('[Enhancer] beforeText:', beforeText, 'afterText:', afterText);
      
      if (!afterText) {
        return false; // We only hard-require AFTER price; BEFORE may be legitimately empty
      }

      // Parse prices to cents for calculations
      const afterCents = parsePriceToCents(afterText);
      const beforeCents = parsePriceToCents(beforeText);

      if (DEBUG) console.debug('[Enhancer] beforeCents/afterCents:', beforeCents, afterCents);
      if (DEBUG) console.debug('[Enhancer] orderLevelRate:', orderLevelRate);

      // Apply order-level discount rate to final after price
      const afterAllCents = Math.round(afterCents * (1 - orderLevelRate));
      writeToAllEnhancedBlocks('.js-review-after-price', formatPrice(afterAllCents));

      // Compare-at container (strikethrough + badge) logic
      let displayBeforeCents = beforeCents;
      let displayBeforeText = beforeText;
      
      // Fallback for cart-discount-only scenarios (frequency 30)
      if (beforeCents === 0 && orderLevelRate > 0 && afterCents > 0 && afterAllCents > 0 && afterAllCents < afterCents) {
        displayBeforeCents = afterCents;
        displayBeforeText = formatPrice(afterCents);
      }
      
      const hasValidBefore = displayBeforeCents > afterAllCents && displayBeforeCents > 0;

      if (hasValidBefore) {
        writeToAllEnhancedBlocks('.js-review-before-price', displayBeforeText);
        toggleCompareAt(true);
        updateBadge(displayBeforeCents, afterAllCents); // uses final after price with order discount
      } else {
        toggleCompareAt(false);               // hides strikethrough + badge
        clearBadge();                         // ensures empty badge content
      }

      // Unhide the enhanced pricing block now that after price is set
      unhideAll('[data-enhance-pricing-target]');

      return true;
    } catch (error) {
      if (DEBUG) console.error('PersonalisedReviewPricingEnhancer: Error in populateFromDom:', error);
      return false;
    }
  }

  /**
   * Handle renderComplete event - populate basic pricing block
   */
  function handleRenderComplete() {
    // one-frame defer to ensure DOM text nodes updated
    requestAnimationFrame(() => {
      const success = populateFromDom(); // read #bundle-total-price / #bundle-before-discount-price, write into all blocks
      const afterOk = hasAfterInBlocks();
      
      if (!success || !afterOk) {
        // tiny retry in case renderComplete fired just before text nodes settled
        setTimeout(() => {
          if (DEBUG) console.debug('[Enhancer] Retry populate after 50ms');
          populateFromDom();
        }, 50);
      }
    });
  }

  /**
   * Handle syncCompleted event - add cart-level discount integration
   */
  function handleSyncCompleted() {
    try {
      // Never show cart-derived UI in Edit mode
      if (window.personalised?.utils?.getMode?.() === 'edit') {
        return;
      }

      if (DEBUG) console.debug('[Enhancer] syncCompleted - refreshing cart data');
      
      // Refresh cart data after sync
      fetchAndProcessCart();

    } catch (error) {
      if (DEBUG) console.error('PersonalisedReviewPricingEnhancer: Error in handleSyncCompleted:', error);
    }
  }

  /**
   * Process cart data and update discount UI
   * @param {Object} cartData - Cart data from /cart.js
   */
  function processCartDiscounts(cartData) {
    try {
      // Reset rate to prevent stale values from previous calculations
      orderLevelRate = 0;
      
      // Calculate order-level discount rate
      const itemsSubtotalCents = cartData.items_subtotal_price || 0;
      const cartOrderDiscountCents = (cartData.cart_level_discount_applications || [])
        .reduce((sum, d) => sum + (d.total_allocated_amount || 0), 0);
      
      // Update module-level rate (clamped to [0, 1])
      orderLevelRate = itemsSubtotalCents > 0 
        ? Math.min(1, Math.max(0, cartOrderDiscountCents / itemsSubtotalCents))
        : 0;

      if (DEBUG) console.debug('[Enhancer] Calculated orderLevelRate:', orderLevelRate);

      // Render cart discounts list (titles only, no amounts)
      renderDiscountsList(cartData.cart_level_discount_applications || []);

      // Trigger price recalculation with new rate
      populateFromDom();

    } catch (error) {
      if (DEBUG) console.error('PersonalisedReviewPricingEnhancer: Error processing cart discounts:', error);
    }
  }

  /**
   * Render cart discounts list
   * @param {Array} discountApplications - Array of discount applications
   */
  function renderDiscountsList(discountApplications) {
    forEachBlock(block => {
      const discountsContainer = block.querySelector(selectors.discountsList);
      if (!discountsContainer) return;

      // Clear previous content
      clearContainer(discountsContainer);

      if (!discountApplications || discountApplications.length === 0) {
        discountsContainer.setAttribute('hidden', '');
        return;
      }

      // Build discount list with cart styling
      const ul = document.createElement('ul');
      ul.className = 'discounts list-unstyled flex gap-1';
      
      discountApplications.forEach(discount => {
        const li = document.createElement('li');
        li.className = 'discount text-sm flex items-center gap-2';
        
        // Add discount icon (proper SVG to match cart)
        const iconSpan = document.createElement('span');
        iconSpan.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-discount icon--extra-small" viewBox="0 0 14 15" fill="none">
          <path d="M2.31547 8.06547C2.23358 7.98349 2.18755 7.87236 2.1875 7.75648V2.6875H7.25648C7.37236 2.68755 7.48349 2.73358 7.56547 2.81547L12.997 8.24703C13.079 8.32907 13.1251 8.44031 13.1251 8.55629C13.1251 8.67227 13.079 8.78351 12.997 8.86555L8.36719 13.497C8.28515 13.579 8.17391 13.6251 8.05793 13.6251C7.94195 13.6251 7.83071 13.579 7.74867 13.497L2.31547 8.06547Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M4.59375 5.75C4.95619 5.75 5.25 5.45619 5.25 5.09375C5.25 4.73131 4.95619 4.4375 4.59375 4.4375C4.23131 4.4375 3.9375 4.73131 3.9375 5.09375C3.9375 5.45619 4.23131 5.75 4.59375 5.75Z" fill="currentColor"/>
        </svg>`;
        iconSpan.setAttribute('aria-hidden', 'true');
        
        const titleSpan = document.createElement('span');
        titleSpan.textContent = discount.title || 'Discount'; // Use textContent for safety
        
        li.appendChild(iconSpan);
        li.appendChild(titleSpan);
        // Note: No amount span per requirements
        ul.appendChild(li);
      });

      discountsContainer.appendChild(ul);
      discountsContainer.removeAttribute('hidden');
    });
  }

  /**
   * Initialize the pricing enhancer
   */
  function init() {
    if (__prEnhancerBound) return; // Already bound
    
    // Check for required dependencies
    if (!window.personalised || !window.personalised.eventBus) {
      if (DEBUG) console.warn('PersonalisedReviewPricingEnhancer: Required dependencies not found');
      return;
    }

    // Bind event listeners idempotently
    window.personalised.eventBus.on('renderComplete', handleRenderComplete);
    window.personalised.eventBus.on('syncCompleted', handleSyncCompleted);
    
    // Set up MutationObserver for live price updates (debounced)
    const debouncedPopulate = debounce(populateFromDom, 50);
    const priceObserver = new MutationObserver(debouncedPopulate);
    
    // Cleanup observer on page unload
    window.addEventListener('beforeunload', () => {
      try { priceObserver.disconnect(); } catch (_) {}
    });
    
    // Observe both price elements for changes
    if (beforePriceEl) {
      priceObserver.observe(beforePriceEl, { childList: true, characterData: true, subtree: true });
    }
    if (afterPriceEl) {
      priceObserver.observe(afterPriceEl, { childList: true, characterData: true, subtree: true });
    }
    
    __prEnhancerBound = true;
    if (DEBUG) console.log('PersonalisedReviewPricingEnhancer: Initialized with live price observer');

    // Initial cart fetch for discount codes
    fetchAndProcessCart();

    // Prime call: if #bundle-total-price already has text, populate immediately
    if (afterPriceEl?.textContent?.trim()) {
      handleRenderComplete();
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
